<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { coreServiceRegistry } from "@core/services/register"
import { formatIdSource } from "@core/helpers"
import type { BaseSummaryLine } from "@core/components/base/summary/BaseSummary.vue"
import type { OwnerOutput } from "@core/composables"
import type { Address } from "@types"
import type {
    ConsentRequestSignResponse,
    CreateConsentRequestSignPayload,
} from "@core/services/consentrequest.service"

// ================================
// TYPES & INTERFACES
// ================================

/** Réponse API create sign (inclut `url` après populateAndFormat métier) */
type InvitationResponse = ConsentRequestSignResponse

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant unique du bâtiment ajoutant le compteur */
        buildingId: string
        /** Adresse du bâtiment */
        address: Address | Record<string, unknown>
        /** Nom figurant sur le contrat (Enedis) */
        contractName?: string
        /** Identifiant réel de la source (PDL ou PCE) */
        idsource: string
        /** Titulaire : user + company optionnelle */
        owner: OwnerOutput
        /** Type de compteur : pdl | pce */
        type?: string
        /** Date de récupération minimale des données (PCE) */
        date?: string
    }>(),
    {
        contractName: "",
        type: "pdl",
        date: "",
    }
)

const emit = defineEmits<{
    back: []
    /**
     * NB (DEV-16347) : nom trompeur conservé pour éviter un renommage large.
     * `sent` ne veut PAS dire « email envoyé » : à ce stade on a seulement
     * GÉNÉRÉ la demande de consentement + le lien (createConsentRequestSign).
     * L'envoi réel de l'email a lieu plus tard dans SourceInvitationSignSent
     * (bouton « Envoyer l'email »). À renommer si on touche au flux (cf.
     * l'événement machine INVITATION_SENT, même malentendu).
     */
    sent: [
        payload: {
            _id_consentrequest: string
            url: string
            _id_source?: string
        },
    ]
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Invitation du titulaire",
            subtitle:
                "Un email sera envoyé au titulaire du compteur pour qu'il signe le consentement.",
            generate: "Générer l'invitation",
            back: "Retour",
            field_titulaire_pro: "Titulaire (professionnel)",
            field_titulaire_particular: "Titulaire (particulier)",
            field_company: "Entreprise",
            field_address: "Adresse",
            field_email: "Email",
            field_contract_name: "Nom du contrat",
            field_idsource_pdl: "Numéro PDL",
            field_idsource_pce: "Numéro PCE",
        },
    },
})

// ================================
// STATE
// ================================

/** Résultat de l’appel createConsentRequestSign, pour émettre sent au succès */
const invitationResult = ref<InvitationResponse | null>(null)

// ================================
// FETCH WRAPPER (createConsentRequestSign)
// ================================

/**
 * Appel API création demande de signature de consentement (invitation).
 * Le résultat est stocké dans invitationResult pour être émis après succès.
 */
async function sendInvitationFn(): Promise<void> {
    const payload: CreateConsentRequestSignPayload = {
        titulaire: {
            user: {
                _id: props.owner.user._id,
                firstname: props.owner.user.firstname,
                lastname: props.owner.user.lastname,
                username: props.owner.user.username,
            },
            company: props.owner.company
                ? {
                      _id: props.owner.company._id,
                      name: props.owner.company.name,
                      type: props.owner.company.type,
                      address: props.owner.company.address ?? null,
                  }
                : null,
        },
        source: { idsource: props.idsource, idtype: props.type },
        address: props.address as Address,
        buildingId: props.buildingId,
        dateFromData: props.date || null,
        isPro: !!props.owner?.company?.name,
    }
    const result = await consentRequestService.createConsentRequestSign(payload)
    invitationResult.value = result
}

const {
    fetch: fetchSendInvitation,
    isFetching,
    hasError,
    error: fetchError,
    isSuccess,
} = useFetchWrapper(sendInvitationFn)

/** Message d’erreur affiché (extrait de l’erreur API) */
const getErrorMessage = computed(() => {
    const err = fetchError.value
    if (!err) return ""
    return (
        (err as Error & { response?: { data?: { message?: string } } })
            ?.response?.data?.message ??
        err.message ??
        "Une erreur est survenue."
    )
})

// Émettre sent lorsque le fetch réussit
watch(isSuccess, (success) => {
    if (success && invitationResult.value) {
        const res = invitationResult.value
        const id = res._id ?? ""
        const path = `/demande/signature-consentement/${id}`
        const url =
            res.url?.trim() ||
            (typeof window !== "undefined"
                ? `${window.location.origin}${path}`
                : path)
        emit("sent", {
            _id_consentrequest: id,
            url,
            _id_source: res.source?._id,
        })
        invitationResult.value = null
    }
})

// ================================
// COMPUTED – champs du résumé
// ================================

/** Lignes du BaseSummary (titulaire, adresse, email, PDL/PCE, etc.) */
const getFields = computed<BaseSummaryLine[]>(() => {
    const ownerLabel = props.owner?.company?.name
        ? t("field_titulaire_pro")
        : t("field_titulaire_particular")
    const lines: BaseSummaryLine[] = [
        {
            label: ownerLabel,
            value: `${props.owner?.user?.firstname ?? ""} ${props.owner?.user?.lastname ?? ""}`.trim(),
        },
        {
            label: t("field_address"),
            value:
                (props.address as Address)?.inline_name ??
                (props.address as Address)?.street ??
                "",
        },
        {
            label: t("field_email"),
            value: props.owner?.user?.username ?? "",
        },
        {
            label:
                props.type === "pce"
                    ? t("field_idsource_pce")
                    : t("field_idsource_pdl"),
            value: formatIdSource(props.idsource, props.type),
        },
    ]
    if (props.owner?.company?.name) {
        lines.unshift({
            label: t("field_company"),
            value: props.owner.company.name,
        })
    }
    if (props.contractName) {
        lines.unshift({
            label: t("field_contract_name"),
            value: props.contractName,
        })
    }
    return lines
})

// ================================
// HANDLERS
// ================================

function onBack(): void {
    emit("back")
}

function onGenerate(): void {
    fetchSendInvitation()
}
</script>

<template>
    <div class="source-invitation flex flex-col gap-4">
        <header class="w-full text-center flex flex-col gap-4">
            <h2 class="text-lg font-semibold text-gray-700 text-center">
                {{ t("title") }}
            </h2>
            <p class="text-sm text-gray-600">
                {{ t("subtitle") }}
            </p>
        </header>

        <BaseSummary :lines="getFields" variant="grey" />

        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('back')"
                severity="secondary"
                :disabled="isFetching"
                @click="onBack"
                class="min-w-[10rem] w-full md:w-auto"
            />
            <BaseButton
                :label="t('generate')"
                :loading="isFetching"
                :disabled="!getFields.length || isFetching"
                @click="onGenerate"
                class="min-w-[10rem] w-full md:w-auto"
            />
        </div>

        <BaseMessage
            v-if="hasError && getErrorMessage"
            severity="error"
            :closable="false"
            class="w-full"
        >
            {{ getErrorMessage }}
        </BaseMessage>
    </div>
</template>
